import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { statusToneInteractiveFieldClasses } from '../../lib/statusTone';

const successFieldClasses = statusToneInteractiveFieldClasses('success');
const warningFieldClasses = statusToneInteractiveFieldClasses('warning');
const errorFieldClasses = statusToneInteractiveFieldClasses('danger');

const textareaVariants = cva(
  'flex w-full rounded-lg border bg-surface text-text-primary placeholder:text-text-tertiary shadow-sm transition-all duration-150 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-50 resize-none',
  {
    variants: {
      variant: {
        default:
          'border-[var(--input-border-default)] hover:border-[var(--input-border-hover)] hover:shadow-md focus:border-[var(--input-border-focus)] focus:shadow-md focus:ring-2 focus:ring-brand-blue-500/30',
        error: cn('focus:ring-2 focus:shadow-md', ...errorFieldClasses),
        success: cn('focus:ring-2 focus:shadow-md', ...successFieldClasses),
        warning: cn('focus:ring-2 focus:shadow-md', ...warningFieldClasses),
      },
      size: {
        sm: 'px-2.5 py-1.5 text-xs',
        md: 'px-3.5 py-2.5 text-sm',
        lg: 'px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    VariantProps<typeof textareaVariants> {
  error?: string;
  helperText?: string;
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      variant,
      size,
      error,
      helperText,
      autoResize = false,
      minRows = 3,
      maxRows = 10,
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const hasError = !!error;
    const effectiveVariant = hasError ? 'error' : variant;
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Auto-resize logic
    React.useEffect(() => {
      if (!autoResize || !textareaRef.current) return;

      const textarea = textareaRef.current;
      textarea.style.height = 'auto';

      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;

      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [value, autoResize, minRows, maxRows]);

    const handleRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }
    };

    const textareaElement = (
      <textarea
        className={cn(textareaVariants({ variant: effectiveVariant, size }), className)}
        ref={handleRef}
        aria-invalid={hasError}
        aria-describedby={
          error ? `${props.id}-error` : helperText ? `${props.id}-helper` : undefined
        }
        rows={!autoResize ? minRows : undefined}
        onChange={handleChange}
        value={value}
        {...props}
      />
    );

    return (
      <div>
        {textareaElement}
        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error && (
              <p id={`${props.id}-error`} className="text-error-500">
                {error}
              </p>
            )}
            {!error && helperText && (
              <p id={`${props.id}-helper`} className="text-text-secondary">
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
